// Course map (docs/02-CURRICULUM.md): units and lesson ids. Ids are stable keys.
// `ready` lessons have authored steps in src/prakrit/lib/content/lessons/*; the rest are planned for later phases.

export type LessonKind = 'learn' | 'practice' | 'test'

export interface LessonMeta {
    id: string
    unitId: string
    kind: LessonKind
    title: { hi: string, en: string }
    minutes: number
    /** Shown only in Adults mode (e.g. विकल्प रूप lessons). */
    adultsOnly?: boolean
    ready: boolean
}

export interface UnitMeta {
    id: string
    number: number
    numeral: string
    title: { hi: string, en: string }
    goal: { hi: string, en: string }
    /** Colour tone used for Kids mode cards. */
    tone: 'saffron' | 'terracotta' | 'teal' | 'green' | 'ink' | 'plum'
    adultsOnly?: boolean
    lessons: LessonMeta[]
}

type Row = [id: string, hi: string, en: string, kind?: LessonKind, minutes?: number, adultsOnly?: boolean]

const unit = (
    number: number,
    numeral: string,
    title: [string, string],
    goal: [string, string],
    tone: UnitMeta['tone'],
    rows: Row[],
    ready: string[] | 'all' = 'all',
    adultsOnly = false
): UnitMeta => {
    const id = `u${number}`

    return {
        id,
        number,
        numeral,
        title: { hi: title[0], en: title[1] },
        goal: { hi: goal[0], en: goal[1] },
        tone,
        adultsOnly,
        lessons: rows.map(([lid, hi, en, kind = 'learn', minutes = 5, onlyAdults]) => ({
            id: lid,
            unitId: id,
            kind,
            title: { hi, en },
            minutes,
            adultsOnly: onlyAdults,
            ready: ready === 'all' || ready.includes(lid)
        }))
    }
}

export const UNITS: UnitMeta[] = [
    unit(0, '०', ['प्राकृत परिचय', 'Meet Prakrit'], ['प्राकृत को पहचानना, उसके अक्षर पढ़ना, और देखना कि संस्कृत/हिन्दी के शब्द प्राकृत में कैसे बदलते हैं।', 'Recognise Prakrit, read its letters, and see how Sanskrit and Hindi words become Prakrit.'], 'saffron', [
        ['u0-kya-hai', 'प्राकृत क्या है?', 'What is Prakrit?', 'learn', 5],
        ['u0-varnamala-svar', 'प्राकृत स्वर', 'Vowels', 'learn', 4],
        ['u0-varnamala-vyanjan', 'प्राकृत व्यंजन', 'Consonants', 'learn', 5],
        ['u0-matra', 'मात्राएँ और अक्षर', 'Vowel signs', 'learn', 5],
        ['u0-padhna', 'शब्द पढ़ो', 'Reading words', 'learn', 4],
        ['u0-dhvani', 'शब्द कैसे बदलते हैं', 'How words change', 'learn', 5],
        ['u0-shabd-bhed', 'शब्द के भेद', 'Kinds of words', 'learn', 5],
        ['u0-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u0-pariksha', 'परखो', 'Unit test', 'test', 7]
    ], ['u0-kya-hai', 'u0-varnamala-svar', 'u0-varnamala-vyanjan', 'u0-matra', 'u0-padhna', 'u0-dhvani', 'u0-shabd-bhed', 'u0-abhyas', 'u0-pariksha']),
    unit(1, '१', ['सर्वनाम एवं पुरुष', 'Pronouns and person'], ['किसी पुरुष और वचन के लिए सही कर्ता चुनना।', 'Pick the right subject for a person and number.'], 'teal', [
        ['u1-aham', 'मैं — अहं', 'I', 'learn', 4],
        ['u1-amhe', 'हम — अम्हे', 'We', 'learn', 4],
        ['u1-tumam', 'तुम — तुमं, तुम्हे', 'You', 'learn', 4],
        ['u1-so-sa', 'वह — सो, सा', 'He, she', 'learn', 5],
        ['u1-imo-ko', 'यह, कौन — इमो, इमा, को, का', 'This, who', 'learn', 5],
        ['u1-purush-vachan', 'पुरुष और वचन', 'The 3 × 2 grid', 'learn', 6],
        ['u1-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u1-pariksha', 'परखो', 'Unit test', 'test', 7]
    ], ['u1-aham', 'u1-amhe', 'u1-tumam', 'u1-so-sa', 'u1-imo-ko', 'u1-purush-vachan', 'u1-abhyas', 'u1-pariksha']),
    unit(2, '२', ['वर्तमान काल', 'Present tense'], ['सभी पुरुषों और वचनों में वर्तमान काल के वाक्य बनाना।', 'Make present-tense sentences in all persons and numbers.'], 'terracotta', [
        ['u2-pahla-vakya', 'मेरा पहला प्राकृत वाक्य', 'My first sentence', 'learn', 5],
        ['u2-dhatu', 'धातु क्या है?', 'What is a root?', 'learn', 4],
        ['u2-ekvachan', 'एकवचन प्रत्यय · मि, सि, इ', 'Singular endings', 'learn', 6],
        ['u2-bahuvachan', 'बहुवचन प्रत्यय · मो, इत्था, न्ति', 'Plural endings', 'learn', 6],
        ['u2-avyaya', 'अव्यय के साथ', 'With adverbs', 'learn', 5],
        ['u2-aakarant', 'आकारान्त धातु', 'Roots in आ, ए, ओ', 'learn', 5],
        ['u2-as', 'अस धातु', 'To be', 'learn', 5],
        ['u2-prashna', 'प्रश्न', 'Questions', 'learn', 5],
        ['u2-vikalp', 'विकल्प रूप', 'Variant forms', 'learn', 4],
        ['u2-abhyas', 'प्रयोग करो', 'Practice', 'practice', 6],
        ['u2-pariksha', 'परखो', 'Unit test', 'test', 7]
    ], ['u2-pahla-vakya', 'u2-dhatu', 'u2-ekvachan', 'u2-bahuvachan', 'u2-avyaya', 'u2-aakarant', 'u2-as', 'u2-prashna', 'u2-vikalp', 'u2-abhyas', 'u2-pariksha']),
    unit(3, '३', ['विधि एवं आज्ञा', 'Wishes and commands'], ['आज्ञा और इच्छा के वाक्य बनाना; मा के साथ निषेध।', 'Make commands and wishes; prohibit with मा.'], 'green', [
        ['u3-pratyay', 'आज्ञा के प्रत्यय', 'Command endings', 'learn', 6],
        ['u3-aakarant', 'आकारान्त धातु की आज्ञा', 'Commands of आ-roots', 'learn', 5],
        ['u3-mat', 'मा — मत करो', 'Do not', 'learn', 4],
        ['u3-vikalp', 'विकल्प रूप', 'Variant forms', 'learn', 4],
        ['u3-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u3-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(4, '४', ['भूत काल', 'Past'], ['बीते काम के वाक्य बनाना।', 'Talk about what happened.'], 'plum', [
        ['u4-iia', 'ईअ — सबके लिए एक रूप', 'One form for all', 'learn', 5],
        ['u4-hi', 'आकारान्त धातु का भूत', 'Past of आ-roots', 'learn', 5],
        ['u4-as-bhoot', 'अस धातु का भूत', 'Was, were', 'learn', 4],
        ['u4-kal', 'कल्ल, पुव्वं', 'Yesterday, before', 'learn', 4],
        ['u4-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u4-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(5, '५', ['भविष्यत् काल', 'Future'], ['आने वाले काम के वाक्य बनाना।', 'Talk about what will happen.'], 'saffron', [
        ['u5-hi', 'भविष्यत् के प्रत्यय', 'Future endings', 'learn', 6],
        ['u5-aakarant', 'आकारान्त धातु का भविष्यत्', 'Future of आ-roots', 'learn', 5],
        ['u5-vikalp', 'विकल्प रूप -स्स-', 'Variant forms', 'learn', 4],
        ['u5-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u5-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(6, '६', ['प्रयोग एवं प्रवीणता', 'Mixing the tenses'], ['चारों कालों को मिलाकर प्रयोग करना।', 'Use all four tenses together.'], 'teal', [
        ['u6-kaal-badlo', 'काल बदलो', 'Change the tense', 'learn', 6],
        ['u6-anuvad-hi-pra', 'हिन्दी से प्राकृत', 'Hindi to Prakrit', 'practice', 6],
        ['u6-anuvad-pra-hi', 'प्राकृत से हिन्दी', 'Prakrit to Hindi', 'practice', 6],
        ['u6-sambandh-kridant', 'पढिऊण — पढ़कर', 'Having read', 'learn', 5],
        ['u6-hetvarth', 'पढिउं — पढ़ने के लिए', 'In order to read', 'learn', 5],
        ['u6-pariksha', 'परखो', 'Mastery test', 'test', 8]
    ]),
    unit(7, '७', ['विभक्ति (संज्ञा)', 'Noun cases'], ['आठ विभक्तियों के रूप और उनके कारक पहचानना।', 'Know the eight case forms and their roles.'], 'terracotta', [
        ['u7-prathama-pu', 'प्रथमा — पुल्लिंग', 'Nominative, masculine', 'learn', 5],
        ['u7-prathama-stri', 'प्रथमा — स्त्रीलिंग', 'Nominative, feminine', 'learn', 5],
        ['u7-prathama-napum', 'प्रथमा — नपुंसकलिंग', 'Nominative, neuter', 'learn', 5],
        ['u7-dvitiya', 'द्वितीया', 'Accusative', 'learn', 5],
        ['u7-tritiya', 'तृतीया', 'Instrumental', 'learn', 5],
        ['u7-chaturthi', 'चतुर्थी', 'Dative', 'learn', 5],
        ['u7-panchami', 'पंचमी', 'Ablative', 'learn', 5],
        ['u7-shashthi', 'षष्ठी', 'Genitive', 'learn', 5],
        ['u7-saptami', 'सप्तमी', 'Locative', 'learn', 5],
        ['u7-sambodhan', 'सम्बोधन', 'Vocative', 'learn', 4],
        ['u7-karak', 'कारक — वाक्य में भूमिका', 'Sentence roles', 'learn', 6],
        ['u7-abhyas', 'प्रयोग करो', 'Practice', 'practice', 6],
        ['u7-pariksha', 'परखो', 'Unit test', 'test', 8]
    ]),
    unit(8, '८', ['सर्वनाम-रूप', 'Pronoun declension'], ['सर्वनामों के सभी विभक्ति-रूप।', 'Every case of the pronouns.'], 'green', [
        ['u8-amh-tumh', 'अम्ह, तुम्ह', 'I and you', 'learn', 6],
        ['u8-ta-ima-ka', 'त, इम, क', 'He, this, who', 'learn', 6],
        ['u8-abhyas', 'प्रयोग करो', 'Practice', 'practice', 5],
        ['u8-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(9, '९', ['संज्ञार्थक क्रिया एवं विशेष शब्द', 'Verbal nouns and special words'], ['धातु से बनी संज्ञाएँ और विशेष शब्द।', 'Nouns made from roots, and special words.'], 'plum', [
        ['u9-pu', 'पुल्लिंग संज्ञाएँ', 'Masculine verbal nouns'],
        ['u9-stri', 'स्त्रीलिंग संज्ञाएँ', 'Feminine verbal nouns'],
        ['u9-napum', 'नपुंसकलिंग संज्ञाएँ', 'Neuter verbal nouns'],
        ['u9-vant', 'भगवंत, अप्पाण, राय', 'Special words'],
        ['u9-pariksha', 'परखो', 'Unit test', 'test', 6]
    ]),
    unit(10, '१०', ['विशेषण एवं संख्या', 'Adjectives and numbers'], ['विशेषण का मेल, तुलना और संख्याएँ।', 'Adjective agreement, comparison and numbers.'], 'saffron', [
        ['u10-gun', 'गुणवाचक विशेषण', 'Agreement'],
        ['u10-tulna', 'तुलना', 'Comparison'],
        ['u10-sankhya-1', 'संख्या १–१८', 'Numbers 1–18'],
        ['u10-sankhya-2', 'संख्या १९–१००', 'Numbers 19–100'],
        ['u10-kram', 'क्रमवाचक', 'Ordinals'],
        ['u10-taddhit', 'तद्धित', 'Derived adjectives'],
        ['u10-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(11, '११', ['कृदन्त', 'Participles'], ['कृदन्त बनाना और संज्ञा के साथ मिलाना।', 'Build participles and agree them with nouns.'], 'teal', [
        ['u11-vartaman', 'वर्तमान कृदन्त', 'Present participle'],
        ['u11-bhoot', 'भूत कृदन्त', 'Past participle'],
        ['u11-bhavishya', 'भविष्य कृदन्त', 'Future participle'],
        ['u11-vidhi', 'विधि कृदन्त', 'Gerundive'],
        ['u11-anukul', 'कृदन्त का मेल', 'Agreement'],
        ['u11-pariksha', 'परखो', 'Unit test', 'test', 7]
    ]),
    unit(12, '१२', ['कर्मवाच्य एवं भाववाच्य', 'Passive and impersonal'], ['कर्मवाच्य और भाववाच्य के वाक्य।', 'Passive and impersonal sentences.'], 'terracotta', [
        ['u12-karmani-vartaman', 'कर्मवाच्य वर्तमान', 'Passive present'],
        ['u12-karmani-bhoot', 'कर्मवाच्य भूत', 'Passive past'],
        ['u12-bhave', 'भाववाच्य', 'Impersonal'],
        ['u12-kridant', 'कर्मवाच्य कृदन्त', 'Passive participles'],
        ['u12-pariksha', 'परखो', 'Unit test', 'test', 6]
    ]),
    unit(13, '१३', ['प्रेरणार्थक', 'Causatives'], ['करवाना — प्रेरणार्थक क्रिया।', 'Making someone do something.'], 'green', [
        ['u13-aav', '-आव प्रत्यय', 'The -आव suffix'],
        ['u13-kaal', 'प्रेरणार्थक के काल', 'Tenses'],
        ['u13-kridant', 'प्रेरक कृदन्त', 'Causative participles'],
        ['u13-vachya', 'प्रेरक वाच्य', 'Causative passive'],
        ['u13-pariksha', 'परखो', 'Unit test', 'test', 6]
    ]),
    unit(14, '१४', ['क्रियातिपत्ति', 'Conditionals'], ['जइ … ता … वाक्य।', 'If … then … sentences.'], 'plum', [
        ['u14-jai-ta', 'जइ … ता …', 'If … then …'],
        ['u14-pariksha', 'परखो', 'Unit test', 'test', 5]
    ]),
    unit(15, '१५', ['सन्धि एवं समास', 'Sandhi and compounds'], ['सन्धि के नियम और छह समास।', 'Sandhi rules and the six compounds.'], 'saffron', [
        ['u15-svar-sandhi', 'स्वर सन्धि', 'Vowel sandhi'],
        ['u15-avyay-sandhi', 'अव्यय सन्धि', 'Sandhi with indeclinables'],
        ['u15-samas', 'समास', 'Compounds'],
        ['u15-pariksha', 'परखो', 'Unit test', 'test', 6]
    ]),
    unit(16, '१६', ['प्राकृत की बोलियाँ', 'Varieties'], ['महाराष्ट्री, शौरसेनी, मागधी, अर्धमागधी, पैशाची, अपभ्रंश की पहचान।', 'Telling the Prakrit varieties apart.'], 'teal', [
        ['u16-boliyan', 'प्राकृत की बोलियाँ', 'Varieties of Prakrit'],
        ['u16-pariksha', 'परखो', 'Unit test', 'test', 5]
    ]),
    unit(17, '१७', ['सरल पाठ एवं आगम', 'Reading real Prakrit'], ['कथाएँ, फिर आगम और साहित्य के अंश।', 'Stories, then passages from the āgamas and literature.'], 'ink', [
        ['u17-path', 'सरल पाठ', 'Graded readings'],
        ['u17-pariksha', 'परखो', 'Final test', 'test', 8]
    ])
]

export const ALL_LESSONS: LessonMeta[] = UNITS.flatMap(u => u.lessons)

export const lessonMeta = (id: string): LessonMeta | undefined => ALL_LESSONS.find(l => l.id === id)

export const unitMeta = (id: string): UnitMeta | undefined => UNITS.find(u => u.id === id)

/** The learning order: units in order, lessons in order. */
export const LESSON_ORDER: string[] = ALL_LESSONS.map(l => l.id)

/** Lessons anyone can open without finishing earlier ones (the home-page entry points and the Devanagari test-out). */
export const ALWAYS_OPEN = new Set(['u0-kya-hai', 'u0-pariksha', 'u2-pahla-vakya'])

export const PASS_MARK = 80

/**
 * Is a lesson open for this learner? Lessons open in order within a unit; a unit opens when the previous unit's
 * test is passed (≥ 80%); a unit test can always be taken once its unit is open (that is how you test out).
 */
export const isUnlocked = (lessonId: string, completed: Record<string, { status: string, best: number }>, extraUnlocks: string[] = []): boolean => {
    if (ALWAYS_OPEN.has(lessonId) || extraUnlocks.includes(lessonId)) return true

    const meta = lessonMeta(lessonId)

    if (!meta) return false

    const u = unitMeta(meta.unitId)

    if (!u) return false

    const done = (id: string) => completed[id]?.status === 'completed'
    const passed = (id: string) => done(id) && (completed[id]?.best ?? 0) >= PASS_MARK

    const unitOpen = (unitIndex: number): boolean => {
        if (unitIndex <= 0) return true

        const startedHere = UNITS[unitIndex].lessons.some(l => done(l.id))

        if (startedHere) return true

        // The previous unit that everyone takes: Adults-only units (इकाई १६) never block the next one.
        let previousIndex = unitIndex - 1

        while (previousIndex > 0 && UNITS[previousIndex].adultsOnly) previousIndex -= 1

        const previous = UNITS[previousIndex]
        const test = previous.lessons.find(l => l.kind === 'test')

        if (test) return passed(test.id)

        // A unit without a test opens the next one once all its lessons are done.
        return previous.lessons.filter(l => !l.adultsOnly).every(l => done(l.id))
    }

    const index = UNITS.indexOf(u)

    if (meta.kind === 'test') return unitOpen(index) || u.lessons.some(l => done(l.id) || ALWAYS_OPEN.has(l.id))

    const position = u.lessons.findIndex(l => l.id === lessonId)

    if (position === 0) return unitOpen(index)

    // Skip Adults-only lessons when looking for the previous lesson, so Kids are never blocked by them.
    for (let i = position - 1; i >= 0; i -= 1) {
        const previous = u.lessons[i]

        if (previous.adultsOnly) continue

        return done(previous.id)
    }

    return unitOpen(index)
}
